import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Provider, EnvironmentProviders, ErrorHandler, provideAppInitializer, LOCALE_ID, inject } from '@angular/core';
import { ENVIRONMENT } from 'shared/environment/environment';
import { BasicErrorHandler } from 'shared/handler/basic-error-handler';
import { httpAuthInterceptor } from 'shared/interceptors/http-auth.interceptor';
import { httpExportInterceptor } from 'shared/interceptors/http-export.interceptor';
import { httpLangInterceptor } from 'shared/interceptors/http-lang.interceptor';
import { httpShopContextInterceptor } from 'shared/interceptors/http-shop-context.interceptor';
import { httpShopStateInterceptor } from 'shared/interceptors/http-shop-state.interceptor';
import { API_BASE_URL } from 'shared/services/INTEGRA_Shop_Client';
import { initializeAppFunction } from './functions';
import { APP_BASE_HREF } from '@angular/common';
import { LoggerApiServiceProvider } from 'shared/services/api/logger/logger-api.service';
import { LoggerServiceProvider } from 'shared/services/infrastructure/logger/logger.service';
import { ConfigurationApiServiceProvider } from 'shared/services/api/configuration/configuration-api.service';
import { ConfigurationServiceProvider } from 'shared/services/infrastructure/configuration/configuration.service';
import { NavigationServiceProvider } from 'shared/services/infrastructure/navigation/navigation.service';
import { ComparisonServiceProvider } from 'shared/services/infrastructure/comparison/comparison.service';
import { UserApiServiceProvider } from 'shared/services/api/user/user-api.service';
import { UserServiceProvider } from 'shared/services/data/user/user.service';
import { CustomTextApiServiceProvider } from 'shared/services/api/custom-text/custom-text-api.service';
import { CustomTextServiceProvider } from 'shared/services/data/custom-text/custom-text.service';
import { ContentApiServiceProvider } from 'shared/services/api/content/content-api.service';
import { ContentServiceProvider } from 'shared/services/data/content/content.service';
import { FavoriteApiServiceProvider } from 'shared/services/api/favorite/favorite-api.service';
import { FavoriteServiceProvider } from 'shared/services/data/favorite/favorite.service';
import { AuthServiceProvider } from 'shared/services/infrastructure/authentication/auth.service';
import { ProductCategoryApiServiceProvider } from 'shared/services/api/product-category/product-category-api.service';
import { AddressApiServiceProvider } from 'shared/services/api/address/address-api.service';
import { AddressServiceProvider } from 'shared/services/data/address/address.service';
import { ShoppingCartApiServiceProvider } from 'shared/services/api/shopping-cart/shopping-cart-api.service';
import { ProductApiServiceProvider } from 'shared/services/api/product/product-api.service';
import { ProductServiceProvider } from 'shared/services/data/product/product.service';
import { OrderPreviewApiServiceProvider } from 'shared/services/api/order-preview/order-preview-api.service';
import { OrderApiServiceProvider } from 'shared/services/api/order/order-api.service';
import { TechGroupApiServiceProvider } from 'shared/services/api/tech-group/tech-group-api.service';
import { TechGroupServiceProvider } from 'shared/services/data/tech-group/tech-group.service';
import { HistoryServiceProvider } from 'shared/services/data/history/history.service';
import { CheckoutServiceProvider } from 'shared/services/data/checkout/checkout.service';
import { INITIALIZABLE_SERVICES } from './initializable.interface';
import { IUserServiceToken } from 'shared/services/data/user/user-service.interface';
import { IAuthServiceToken } from 'shared/services/infrastructure/authentication/auth-service.interface';
import { LocalStorageServiceProvider } from 'shared/services/infrastructure/localstorage/localstorage.service';
import { ViewTypeServiceProvider } from 'shared/services/ui/viewType/view-type.service';
import { AssetsServiceProvider } from 'shared/services/ui/assets/assets.service';
import { CookieBannerServiceProvider } from 'shared/services/ui/cookie-banner/cookie-banner.service';
import { MediaServiceProvider } from 'shared/services/ui/media/media.service';
import { TooltipServiceProvider } from 'shared/services/ui/tooltip/tooltip.service';
import { ToastServiceProvider } from 'shared/services/ui/toast/toast.service';
import { SidebarCartServiceProvider } from 'shared/services/ui/sidebar/cart/sidebar-cart.service';
import { PaginationServiceProvider } from 'shared/services/ui/pagination/pagination.service';
import { OverlayServiceProvider } from 'shared/services/ui/overlay/overlay.service';
import { PriceTemplateServiceProvider } from 'shared/services/ui/price-template/price-template.service';
import { AddressSelectDialogServiceProvider } from 'shared/services/ui/dialogs/address-select/address-select-dialog.service';
import { AddressAddDialogServiceProvider } from 'shared/services/ui/dialogs/address-add/address-add-dialog.service';
import { BannerServiceProvider } from 'shared/services/ui/banner/banner.service';

export const LANGUAGE_CODE = 'LANGUAGE_CODE';
export const REGION_CODE = 'REGION_CODE';

const LOCALEDELIMITER = '-';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const CoreProviders: (Provider | EnvironmentProviders)[] = [
  {
    provide: APP_BASE_HREF,
    useFactory: (languageCode: string) => {
      return ENVIRONMENT.appBaseHref + "/" + languageCode + "/";
    },
    deps: [LANGUAGE_CODE]
  },
  {
    provide: LANGUAGE_CODE,
    useFactory: (locale: string) => locale.split(LOCALEDELIMITER)[0],
    deps: [LOCALE_ID]
  },
  {
    provide: REGION_CODE,
    useFactory: (locale: string) => locale.split(LOCALEDELIMITER)[1] || '',
    deps: [LOCALE_ID]
  },
  { provide: API_BASE_URL, useFactory: () => ENVIRONMENT.apiBaseUrl },

  { provide: ErrorHandler, useClass: BasicErrorHandler },

  /**
   * Order of declaration = order of chain
   * request -> 1 -> 2 -> ... -> server -> ... -> 2 -> 1 ... -> response
   */
  provideHttpClient(withInterceptors([
    httpShopStateInterceptor,
    httpLangInterceptor,
    httpShopContextInterceptor,
    httpAuthInterceptor,
    httpExportInterceptor
  ])),

  LoggerApiServiceProvider,
  LoggerServiceProvider,

  ConfigurationApiServiceProvider,
  ConfigurationServiceProvider,

  NavigationServiceProvider,

  ComparisonServiceProvider,

  UserApiServiceProvider,
  UserServiceProvider,

  CustomTextApiServiceProvider,
  CustomTextServiceProvider,

  ContentApiServiceProvider,
  ContentServiceProvider,

  FavoriteApiServiceProvider,
  FavoriteServiceProvider,

  AuthServiceProvider,

  ProductCategoryApiServiceProvider,

  AddressApiServiceProvider,
  AddressServiceProvider,

  ShoppingCartApiServiceProvider,

  ProductApiServiceProvider,
  ProductServiceProvider,

  OrderPreviewApiServiceProvider,

  OrderApiServiceProvider,

  TechGroupApiServiceProvider,
  TechGroupServiceProvider,

  HistoryServiceProvider,

  CheckoutServiceProvider,

  LocalStorageServiceProvider,

  AssetsServiceProvider,
  CookieBannerServiceProvider,
  MediaServiceProvider,
  OverlayServiceProvider,
  PaginationServiceProvider,
  PriceTemplateServiceProvider,
  SidebarCartServiceProvider,
  ToastServiceProvider,
  TooltipServiceProvider,
  ViewTypeServiceProvider,
  AddressAddDialogServiceProvider,
  AddressSelectDialogServiceProvider,
  BannerServiceProvider,

  // additional method called upon app initialization
  provideAppInitializer(() => {
    let initializable = inject(INITIALIZABLE_SERVICES);
    let userService = inject(IUserServiceToken);
    let authService = inject(IAuthServiceToken);
    return initializeAppFunction(initializable, userService, authService);
  })
];
